// src/pages/admin/AuditLog.jsx
import { useState, useMemo, useCallback } from "react";
import {
  Badge,
  Button,
  ColumnToggle,
  DataPageCard,
  Dropdown,
  FormField,
  PaginatedTable,
  SearchInput,
} from "@common";
import { useAuditLogs } from "@api/hooks";
import { ACTION_OPTIONS, ACTION_COLORS, ENDPOINT_LABELS, WHERE_OPTIONS } from "@utils/contants";

function getEndpointLabel(endpoint) {
  if (!endpoint) return "—";
  // Strip leading /api/ and match against known segments
  const clean = endpoint.replace(/^\/api\//, "");
  const normalized = clean.replace(/_/g, "-");
  for (const [key, label] of Object.entries(ENDPOINT_LABELS)) {
    const normalizedKey = key.replace(/_/g, "-");
    if (normalized.startsWith(normalizedKey)) return label;
  }
  // Fallback: capitalize first segment
  const first = clean.split("/")[0];
  return first
    .replace(/_/g, "-")
    .split("-")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function formatTimestamp(ts) {
  if (!ts) return "—";
  const d = new Date(ts);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export default function AuditLog() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [actionFilter, setActionFilter] = useState("");
  const [userEmailFilter, setUserEmailFilter] = useState("");
  const [whereFilter, setWhereFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [hiddenColumns, setHiddenColumns] = useState(() => {
    try {
      const saved = localStorage.getItem("auditLog_hiddenColumns");
      return saved ? new Set(JSON.parse(saved)) : new Set();
    } catch {
      return new Set();
    }
  });

  const filters = useMemo(() => {
    const f = {
      limit: pageSize,
      offset: (page - 1) * pageSize,
    };
    if (actionFilter) f.action = actionFilter;
    if (userEmailFilter) f.user_email = userEmailFilter;
    if (whereFilter) f.endpoint = whereFilter;
    if (statusFilter) f.status = statusFilter.toLowerCase();
    if (startDate) f.start_date = startDate;
    if (endDate) f.end_date = endDate;
    return f;
  }, [
    page,
    pageSize,
    actionFilter,
    userEmailFilter,
    whereFilter,
    statusFilter,
    startDate,
    endDate,
  ]);

  const { data, isLoading, error } = useAuditLogs(filters, { enablePolling: true });

  const logs = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  const allColumns = useMemo(
    () => [
      {
        key: "timestamp",
        header: "Date & Time",
        nowrap: true,
        render: (r) => (
          <span className="text-xs text-palette-gray-badge">{formatTimestamp(r.timestamp)}</span>
        ),
      },
      {
        key: "user_email",
        header: "User",
        render: (r) => (
          <div>
            <span className="text-sm font-medium text-palette-gray-text">
              {r.user_name || r.user_email}
            </span>
            {r.user_role && (
              <span className="ml-2 text-xs text-palette-gray-placeholder">({r.user_role})</span>
            )}
            {r.user_name && r.user_name !== r.user_email && (
              <div className="text-xs text-palette-gray-placeholder">{r.user_email}</div>
            )}
          </div>
        ),
      },
      {
        key: "action",
        header: "Action",
        nowrap: true,
        render: (r) => <Badge color={ACTION_COLORS[r.action] || "default"}>{r.action}</Badge>,
      },
      {
        key: "where",
        header: "Where",
        nowrap: true,
        render: (r) => (
          <span className="text-sm text-palette-gray-heading">{getEndpointLabel(r.endpoint)}</span>
        ),
      },
      {
        key: "method",
        header: "Method",
        nowrap: true,
        render: (r) => (
          <span className="rounded bg-palette-gray-subtle px-2 py-0.5 text-xs font-mono font-semibold text-palette-gray-badge">
            {r.method}
          </span>
        ),
      },
      {
        key: "endpoint",
        header: "Endpoint",
        render: (r) => (
          <span className="text-sm text-palette-gray-heading break-all">{r.endpoint}</span>
        ),
      },
      {
        key: "response_status",
        header: "Status",
        nowrap: true,
        render: (r) => {
          const code = r.response_status;
          const color = code >= 200 && code < 300 ? "success" : code >= 400 ? "danger" : "warning";
          return <Badge color={color}>{code}</Badge>;
        },
      },
      {
        key: "error_message",
        header: "Error Message",
        render: (r) => {
          if (!r.error_message) return <span className="text-palette-gray-placeholder">—</span>;
          return (
            <pre className="text-xs text-palette-red-badge whitespace-pre-wrap break-all max-w-xs max-h-24 overflow-auto">
              {r.error_message}
            </pre>
          );
        },
      },
      {
        key: "request_data",
        header: "Request Body",
        render: (r) => {
          if (!r.request_data || Object.keys(r.request_data).length === 0)
            return <span className="text-palette-gray-placeholder">—</span>;
          return (
            <pre className="text-xs text-palette-gray-badge whitespace-pre-wrap break-all max-w-xs max-h-24 overflow-auto">
              {JSON.stringify(r.request_data, null, 2)}
            </pre>
          );
        },
      },
    ],
    [],
  );

  const columns = useMemo(
    () => allColumns.filter((c) => !hiddenColumns.has(c.key)),
    [allColumns, hiddenColumns],
  );

  const toggleColumn = useCallback((key) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      localStorage.setItem("auditLog_hiddenColumns", JSON.stringify([...next]));
      return next;
    });
  }, []);

  function resetFilters() {
    setActionFilter("");
    setUserEmailFilter("");
    setWhereFilter("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  }

  const hasFilters =
    actionFilter || userEmailFilter || whereFilter || statusFilter || startDate || endDate;

  return (
    <div className="mt-2">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-lg font-bold">Tracking API (This one is for Administrators ONLY)</h2>
        <ColumnToggle
          allColumns={allColumns}
          hiddenColumns={hiddenColumns}
          onChange={toggleColumn}
        />
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Dropdown
          label="Action"
          options={ACTION_OPTIONS}
          value={actionFilter}
          onChange={(v) => {
            setActionFilter(v);
            setPage(1);
          }}
          placeholder=""
        />

        <SearchInput
          value={userEmailFilter}
          onChange={(v) => {
            setUserEmailFilter(v);
            setPage(1);
          }}
          placeholder="Search email..."
        />

        <Dropdown
          label="Where"
          options={WHERE_OPTIONS}
          value={whereFilter}
          onChange={(v) => {
            setWhereFilter(v);
            setPage(1);
          }}
          placeholder=""
        />

        <Dropdown
          label="Status"
          options={["Success", "Failed"]}
          value={statusFilter}
          onChange={(v) => {
            setStatusFilter(v);
            setPage(1);
          }}
          placeholder=""
        />

        <FormField
          label="From"
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            setPage(1);
          }}
        />

        <FormField
          label="To"
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
            setPage(1);
          }}
        />

        {hasFilters && (
          <Button variant="outline" size="sm" onClick={resetFilters}>
            Clear Filters
          </Button>
        )}
      </div>

      <DataPageCard
        isLoading={isLoading}
        error={error}
        isEmpty={!isLoading && logs.length === 0}
        emptyMessage="No audit logs found."
        loadingMessage="Loading audit logs..."
        showLoadingSpinner={true}
      >
        <PaginatedTable
          mode="server"
          columns={columns}
          data={logs}
          rowKey={(r) => r.id}
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          total={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(n) => {
            setPage(1);
            setPageSize(n);
          }}
        />
      </DataPageCard>
    </div>
  );
}
